import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { getNewsletterSubscribersByFilter } from "../../../services/newsletterServices";
import { useUI } from "../../Context/UIContext";
import { SUBSCRIBER_ROLE_OPTIONS, SubscriberRoleChip } from "./subscriberRoleUtils";
import useEditorViewportHeight from "../newsletter-editor/hooks/useEditorViewportHeight";
import ContentSectionPanel from "../content/ContentSectionPanel";
import ContentSectionCard from "../content/ContentSectionCard";
import {
  contentFieldSx,
  contentTablePaginationFooterSx,
  contentViewportPanelSx,
} from "../content/contentEditorStyles";
import { formatValue } from "../../../utils/formatters";
import { adminDataTableHeadSx } from "../../../utils/adminTableScrollSx";

const SESSION_KEY = "newsletter_filters";
const SORT_EMAIL = "EMAIL";
const PAGE_SIZE = 50;

const iscrittiTableHeadSx = {
  ...adminDataTableHeadSx,
  "& .MuiTableRow-head .MuiTableCell-head:first-of-type": {
    borderTopLeftRadius: 12,
  },
  "& .MuiTableRow-head .MuiTableCell-head:last-of-type": {
    borderTopRightRadius: 12,
  },
};

const iscrittiTableSx = {
  width: "100%",
  tableLayout: "fixed",
};

const emailCellSx = {
  py: 0.75,
  maxWidth: 0,
  overflow: "hidden",
};

const emailTextSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
  minWidth: 0,
};

const getInitial = () => {
  const saved = sessionStorage.getItem(SESSION_KEY);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return {};
    }
  }
  return {};
};

const NewsletterIscrittiTab = () => {
  const { showError } = useUI();
  const panelRef = useRef(null);
  const panelHeight = useEditorViewportHeight(panelRef);
  const initial = getInitial();

  const [filterInput, setFilterInput] = useState(initial.query || "");
  const filterRef = useRef(null);
  const [subscriberList, setSubscriberList] = useState([]);
  const [page, setPage] = useState(initial.page || 0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortDirection, setSortDirection] = useState(initial.sortDirection || "asc");
  const [typeFilters, setTypeFilters] = useState(initial.typeFilters || []);
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        query: filterInput,
        page,
        sortDirection,
        typeFilters,
      })
    );
  }, [filterInput, page, sortDirection, typeFilters]);

  const fetchData = useCallback(async (searchQuery, currentPage, ord, subscriberTypes) => {
    setTableLoading(true);
    try {
      const data = await getNewsletterSubscribersByFilter(
        searchQuery,
        currentPage,
        PAGE_SIZE,
        ord,
        SORT_EMAIL,
        subscriberTypes
      );
      setSubscriberList(data.newsletterSubscribers || []);
      setTotalCount(Number(data.countNewsletters) || 0);
    } catch (err) {
      setSubscriberList([]);
      setTotalCount(0);
      showError(err.message);
    } finally {
      setTableLoading(false);
    }
  }, [showError]);

  const lastParamsRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const query = filterInput.trim();
      const last = lastParamsRef.current;
      const changed =
        query !== last?.query ||
        page !== last?.page ||
        sortDirection !== last?.sortDirection ||
        JSON.stringify(typeFilters) !== JSON.stringify(last?.typeFilters);

      if (changed) {
        fetchData(query, page, sortDirection, typeFilters);
        lastParamsRef.current = {
          query,
          page,
          sortDirection,
          typeFilters,
        };
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchData, filterInput, page, sortDirection, typeFilters]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasActiveFilters = filterInput.trim() || typeFilters.length > 0;

  const handleFilter = (event) => {
    event.preventDefault();
    filterRef.current?.blur();
    setPage(0);
  };

  const handleRequestSort = () => {
    setPage(0);
    setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
  };

  const resetFilters = () => {
    setFilterInput("");
    setTypeFilters([]);
    setSortDirection("asc");
    setPage(0);
  };

  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={2} align="center" sx={{ py: 0, border: "none" }}>
        <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <SearchOffIcon sx={{ fontSize: 48, color: "#a0aec0" }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Nessun iscritto trovato
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Prova a modificare i filtri di ricerca
          </Typography>
          {hasActiveFilters ? (
            <Button variant="outlined" onClick={resetFilters} sx={{ borderColor: "#0b3d87", color: "#0b3d87" }}>
              Reset filtri
            </Button>
          ) : null}
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderToolbar = () => (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1, justifyContent: "flex-end" }}>
      <FormControl size="small" sx={{ minWidth: 160, ...contentFieldSx }}>
        <Select
          multiple
          displayEmpty
          value={typeFilters}
          onChange={(event) => {
            setTypeFilters(event.target.value);
            setPage(0);
          }}
          renderValue={(selected) =>
            selected.length === 0
              ? "Tutti i tipi"
              : selected
                  .map((value) => SUBSCRIBER_ROLE_OPTIONS.find((option) => option.value === value)?.label || value)
                  .join(", ")
          }
        >
          {SUBSCRIBER_ROLE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box component="form" onSubmit={handleFilter} sx={{ minWidth: { xs: 160, sm: 220 }, flex: { xs: "1 1 160px", sm: "0 1 280px" } }}>
        <TextField
          fullWidth
          size="small"
          inputRef={filterRef}
          placeholder="Cerca per email..."
          value={filterInput}
          onChange={(event) => {
            setFilterInput(event.target.value);
            setPage(0);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#64748b" }} />
                </InputAdornment>
              ),
            },
          }}
          sx={contentFieldSx}
        />
      </Box>
      {hasActiveFilters ? (
        <Tooltip title="Reset filtri">
          <IconButton size="small" onClick={resetFilters} aria-label="Reset filtri">
            <FilterAltOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}>
        {totalCount} iscritti
      </Typography>
    </Stack>
  );

  return (
    <Box ref={panelRef} sx={contentViewportPanelSx(panelHeight)}>
      <ContentSectionPanel title="Iscritti" toolbar={renderToolbar()} fillBody panelScroll>
        <ContentSectionCard noPadding sx={{ width: "100%" }}>
          {tableLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Table sx={iscrittiTableSx}>
              <TableHead sx={iscrittiTableHeadSx}>
                <TableRow>
                  <TableCell sx={{ py: 1, width: "28%", whiteSpace: "nowrap" }}>Tipo</TableCell>
                  <TableCell sx={{ py: 1, width: "72%", maxWidth: 0 }}>
                    <TableSortLabel
                      active
                      direction={sortDirection}
                      onClick={handleRequestSort}
                      sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriberList.length === 0
                  ? renderEmptyState()
                  : subscriberList.map((row, index) => (
                      <TableRow key={row.id} sx={{ backgroundColor: index % 2 === 0 ? "white" : "#f8fafc" }}>
                        <TableCell sx={{ py: 0.75, whiteSpace: "nowrap" }}>
                          <SubscriberRoleChip role={row.role} />
                        </TableCell>
                        <TableCell sx={emailCellSx}>
                          <Tooltip title={row.email || ""}>
                            <Typography variant="body2" sx={emailTextSx}>
                              {formatValue(row.email)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          )}
          {totalCount > PAGE_SIZE ? (
            <Box sx={contentTablePaginationFooterSx}>
              <Pagination count={totalPages} page={page + 1} onChange={(_, value) => setPage(value - 1)} size="small" />
            </Box>
          ) : null}
        </ContentSectionCard>
      </ContentSectionPanel>
    </Box>
  );
};

export default NewsletterIscrittiTab;
